// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { TerceraChartUtils } from '@front/chart/TerceraChartUtils';
import { TerceraChartDrawingType } from '@front/chart/Utils/ChartConstants';
import TerceraAddOverlayScreenOutScreensTemplate from '@front/templates/TerceraAddOverlayScreenOutScreensTemplate.html';
import TerceraAddOverlayScreenTemplate from '@front/templates/TerceraAddOverlayScreenTemplate.html';
import { DataCache } from '@shared/commons/DataCache';
import { PairColor } from '@shared/commons/DynProperty';
import { addOverlayScreenHandler } from '@shared/utils/AppHandlers';
import { Point } from '@shared/commons/Geometry';
import { Resources } from '@shared/localizations/Resources';
import { NewContextMenuItemType } from '@shared/utils/ContextMenuUtils';
import { HistoryType } from '@shared/utils/History/HistoryType';
import { Periods } from '@shared/utils/History/TFInfo';
import { TerceraMessageBox } from './TerceraMessageBox';
import { TerceraWindowBase } from './TerceraWindowBase';
import { ThemeManager } from '../misc/ThemeManager';
import { ScreensNames } from '../UtilsClasses/FactoryConstants';
import { MainWindowManager } from '../UtilsClasses/MainWindowManager';
let instance = null;
export class TerceraAddOverlayScreen extends TerceraWindowBase {
    constructor() {
        super(...arguments);
        this.callback = null;
        this.onShowPriceMarkerChanged = (event) => {
            void this.set('showPriceMarker', event.currentTarget.checked);
        };
    }
    getType() {
        return ScreensNames.AddOverlayScreen;
    }
    oninit() {
        super.oninit();
        this.onColorPickerClicked = this.onColorPickerClicked.bind(this);
        void this.set('header', Resources.getResource('OverlayScreen.name'));
        this.on('okEvent', this.okClick);
        this.on('cancelEvent', this.close);
    }
    oncomplete() {
        super.oncomplete();
        this.Controls.barsBodyPicker.set('pairColor', new PairColor(ThemeManager.colors.Chart_BarsUpColor, ThemeManager.colors.Chart_BarsDownColor));
        this.Controls.barsBorderPicker.set('pairColor', new PairColor(ThemeManager.colors.Chart_BarsUpBorderColor, ThemeManager.colors.Chart_BarsDownBorderColor));
        this.Controls.wickPicker.set('pairColor', new PairColor(ThemeManager.colors.Chart_WickUpBorderColor, ThemeManager.colors.Chart_WickDownBorderColor));
        this.Controls.dojiPicker.set('coloringButtonColor', ThemeManager.colors.Chart_BarsHiLowColor);
        this.Controls.histogramPicker.set('coloringButtonColor', ThemeManager.colors.Chart_HistogramLineColor);
        this.Controls.linePicker.set('coloringButtonColor', ThemeManager.colors.Chart_SolidPriceColor);
        this.Controls.solidPricePicker.set('coloringButtonColor', ThemeManager.colors.Chart_SolidPriceColor);
        this.populateSymbols();
        this.Controls.insLookup.onChangeValue.Subscribe(this.onInstrumentChanged, this);
        const styleCombo = this.Controls.chartStyleComboBox;
        const arr = [];
        if (!Resources.isHidden('chart.tool.line')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.line',
                value: TerceraChartDrawingType.Line,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.Line)
            });
        }
        // ---------------------------------------------------------------------------------------------
        if (!Resources.isHidden('chart.tool.bar')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.bar',
                value: TerceraChartDrawingType.Bar,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.Bar)
            });
        }
        // ---------------------------------------------------------------------------------------------
        if (!Resources.isHidden('chart.tool.candle')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.candle',
                value: TerceraChartDrawingType.Candle,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.Candle)
            });
        }
        // ---------------------------------------------------------------------------------------------
        if (!Resources.isHidden('chart.tool.dot')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.dot',
                value: TerceraChartDrawingType.Dot,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.Dot)
            });
        }
        // ---------------------------------------------------------------------------------------------
        if (!Resources.isHidden('chart.tool.dotline')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.dotline',
                value: TerceraChartDrawingType.DotLine,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.DotLine)
            });
        }
        // ---------------------------------------------------------------------------------------------
        if (!Resources.isHidden('chart.tool.forest')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.forest',
                value: TerceraChartDrawingType.Forest,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.Forest)
            });
        }
        // ---------------------------------------------------------------------------------------------
        if (!Resources.isHidden('chart.tool.solid')) {
            arr.push({
                type: NewContextMenuItemType.Default,
                text: 'chart.tool.solid',
                value: TerceraChartDrawingType.Solid,
                svgImage: TerceraChartUtils.getChartDrawingIconByType(TerceraChartDrawingType.Solid)
            });
        }
        styleCombo.set('items', arr);
        styleCombo.setItembyValue(TerceraChartDrawingType.Line);
        const priceScalingCombo = this.Controls.priceScalingComboBox;
        const priceScalingComboboxItems = [
            { type: NewContextMenuItemType.Default, text: 'OverlayScreen.Main scale', value: false },
            { type: NewContextMenuItemType.Default, text: 'OverlayScreen.Separate scale', value: true }
        ];
        priceScalingCombo.set('items', priceScalingComboboxItems);
        priceScalingCombo.setItembyValue(true);
        this.center();
        this.localize();
    }
    localize() {
        void this.set({
            okButtonText: Resources.getResource('OverlayScreen.okButton'),
            cancelButtonText: Resources.getResource('OverlayScreen.cancelButton')
        });
    }
    dispose() {
        super.dispose();
        const insLookup = this.Controls.insLookup;
        if (insLookup) {
            insLookup.onChangeValue.UnSubscribe(this.onInstrumentChanged, this);
        }
        if (!isNullOrUndefined(this.closeCallback)) {
            this.closeCallback();
        }
        this.callback = null;
    }
    okClick() {
        if (this.callback) {
            this.callback({
                instrument: this.Controls.insLookup.get('selectedItem'),
                chartDrawingType: this.Controls.chartStyleComboBox.get('selectedItem').value,
                priceScaling: this.Controls.priceScalingComboBox.get('selectedItem').value,
                historyType: this.Controls.sourceTypeComboBox.get('selectedItem').value,
                showPriceMarker: this.get('showPriceMarker'),
                barsBodyPairColor: this.Controls.barsBodyPicker.get('pairColor'),
                barsBorderPairColor: this.Controls.barsBorderPicker.get('pairColor'),
                wickPairColor: this.Controls.wickPicker.get('pairColor'),
                solidPriceColor: this.Controls.solidPricePicker.get('coloringButtonColor'),
                lineColor: this.Controls.linePicker.get('coloringButtonColor'),
                HistogramLineColor: this.Controls.histogramPicker.get('coloringButtonColor'),
                Doji: this.Controls.dojiPicker.get('coloringButtonColor')
            });
        }
        this.close();
    }
    // TODO.
    populateSymbols() {
        /* symbolsLookup.SetPopulationFlags(false, true, true, true);
    symbolsLookup.Populate(); */
    }
    onInstrumentChanged(newIns) {
        if (!newIns)
            return;
        const sourceTypeCombo = this.Controls.sourceTypeComboBox;
        sourceTypeCombo.setItembyValue(newIns.DefaultChartHistoryType);
    }
    populateSourceType(chart) {
        if (!chart)
            return;
        const period = chart.TimeFrameInfo().Periods; // текущий период
        const types = DataCache.GetAllowedHistoryTypesByPeriod(period);
        types.sort(historyTypeComparer.compare);
        const types_len = types.length;
        const sourceTypeItems = [];
        for (let i = 0; i < types_len; i++) {
            const type = types[i];
            sourceTypeItems.push({
                type: NewContextMenuItemType.Default,
                text: (period === Periods.TIC || period < 0) && type === 1
                    ? 'chart.view.sourceBID/ASK'
                    : 'chart.view.source' + type,
                value: type
            });
        }
        const sourceTypeCombo = this.Controls.sourceTypeComboBox;
        sourceTypeCombo.set('items', sourceTypeItems);
        sourceTypeCombo.setItembyValue(sourceTypeItems[0].value);
    }
    populate(overlay) {
        if (overlay.ActiveInstrument) {
            this.Controls.insLookup.set('selectedItem', overlay.ActiveInstrument);
        }
        this.Controls.sourceTypeComboBox.setItembyValue(overlay.HistoryType);
        this.Controls.chartStyleComboBox.setItembyValue(overlay.ChartDrawingTypeGet());
        this.Controls.priceScalingComboBox.setItembyValue(overlay.separateScale);
        const mainPriceRenderer = overlay.mainPriceRenderer;
        this.Controls.barsBodyPicker.set('pairColor', new PairColor(mainPriceRenderer.barsUpColorBrush.Color, mainPriceRenderer.barsDownColorBrush.Color));
        this.Controls.barsBorderPicker.set('pairColor', new PairColor(mainPriceRenderer.barsUpBorderColorPen.Color, mainPriceRenderer.barsDownBorderColorPen.Color));
        this.Controls.wickPicker.set('pairColor', new PairColor(mainPriceRenderer.wickUpBorderColorPen.Color, mainPriceRenderer.wickDownBorderColorPen.Color));
        this.Controls.dojiPicker.set('coloringButtonColor', mainPriceRenderer.barsHiLowColorPen.Color);
        this.Controls.histogramPicker.set('coloringButtonColor', mainPriceRenderer.histogramLineColorPen.Color);
        this.Controls.linePicker.set('coloringButtonColor', overlay.MarkerAndLineColorGet());
        this.Controls.solidPricePicker.set('coloringButtonColor', mainPriceRenderer.SolidPriceBrushColor);
        this.set('showPriceMarker', overlay.ShowMarker);
    }
    onColorPickerClicked(color, x, y, onColorChanged) {
        const colorPickerProps = {
            color,
            x,
            y,
            parentLoc: new Point(this.get('left'), this.get('top')),
            onColorChangedCallback: onColorChanged,
            onCloseColorPicker: () => {
                void this.set('colorPickerProps', null);
            }
        };
        void this.set({ colorPickerProps });
    }
    static addNewOverlay(chart, closeCallback) {
        if (chart && chart.Overlays.length > 4) {
            TerceraMessageBox.Show(Resources.getResource('OverlayScreen.Overlays'), Resources.getResource('OverlayScreen.numberOverlaysError'), null, null, null, false, true);
            return;
        }
        const callback = (params) => {
            TerceraAddOverlayScreen.onAddNewOverlay(params, chart);
        };
        TerceraAddOverlayScreen.show(chart, callback, closeCallback);
    }
    static onAddNewOverlay(params, chart) {
        const instrument = params.instrument;
        const chartDrawingType = params.chartDrawingType;
        const historyType = params.historyType;
        const showPriceMarker = params.showPriceMarker;
        const priceScaling = params.priceScaling;
        const barsBodyPairColor = params.barsBodyPairColor;
        const barsBorderPairColor = params.barsBorderPairColor;
        const wickPairColor = params.wickPairColor;
        const solidPriceColor = params.solidPriceColor;
        const lineColor = params.lineColor;
        const HistogramLineColor = params.HistogramLineColor;
        const Doji = params.Doji;
        const overlay = chart.AddOverlayInstrument(instrument.GetInteriorID(), true, chartDrawingType, historyType);
        if (!overlay)
            return;
        const mainPriceRenderer = overlay.mainPriceRenderer;
        mainPriceRenderer.barsUpColorBrush.Color = barsBodyPairColor.Color1;
        mainPriceRenderer.barsDownColorBrush.Color = barsBodyPairColor.Color2;
        mainPriceRenderer.barsUpBorderColorPen.Color = barsBorderPairColor.Color1;
        mainPriceRenderer.barsDownBorderColorPen.Color = barsBorderPairColor.Color2;
        mainPriceRenderer.wickUpBorderColorPen.Color = wickPairColor.Color1;
        mainPriceRenderer.wickDownBorderColorPen.Color = wickPairColor.Color2;
        mainPriceRenderer.SolidPriceBrushColor = solidPriceColor;
        mainPriceRenderer.histogramLineColorPen.Color = HistogramLineColor;
        mainPriceRenderer.barsHiLowColorPen.Color = Doji;
        overlay.MarkerAndLineColorSet(lineColor);
        overlay.ShowMarker = showPriceMarker;
        overlay.separateScale = priceScaling;
    }
    static editOverlay(chart, overlay) {
        const callback = (params) => {
            TerceraAddOverlayScreen.onEditOverlay(params, chart, overlay);
        };
        TerceraAddOverlayScreen.show(chart, callback);
        instance.on('complete', function () {
            instance.populate(overlay);
        });
    }
    static onEditOverlay(params, chart, overlay) {
        const instrument = params.instrument;
        const chartDrawingType = params.chartDrawingType;
        const historyType = params.historyType;
        const showPriceMarker = params.showPriceMarker;
        const priceScaling = params.priceScaling;
        const barsBodyPairColor = params.barsBodyPairColor;
        const barsBorderPairColor = params.barsBorderPairColor;
        const wickPairColor = params.wickPairColor;
        const solidPriceColor = params.solidPriceColor;
        const lineColor = params.lineColor;
        const HistogramLineColor = params.HistogramLineColor;
        const Doji = params.Doji;
        overlay.Instrument = instrument.GetInteriorID();
        overlay.HistoryType = historyType;
        overlay.ChartDrawingTypeSet(chartDrawingType);
        const mainPriceRenderer = overlay.mainPriceRenderer;
        mainPriceRenderer.barsUpColorBrush.Color = barsBodyPairColor.Color1;
        mainPriceRenderer.barsDownColorBrush.Color = barsBodyPairColor.Color2;
        mainPriceRenderer.barsUpBorderColorPen.Color = barsBorderPairColor.Color1;
        mainPriceRenderer.barsDownBorderColorPen.Color = barsBorderPairColor.Color2;
        mainPriceRenderer.wickUpBorderColorPen.Color = wickPairColor.Color1;
        mainPriceRenderer.wickDownBorderColorPen.Color = wickPairColor.Color2;
        mainPriceRenderer.SolidPriceBrushColor = solidPriceColor;
        mainPriceRenderer.histogramLineColorPen.Color = HistogramLineColor;
        mainPriceRenderer.barsHiLowColorPen.Color = Doji;
        overlay.MarkerAndLineColorSet(lineColor);
        overlay.ShowMarker = showPriceMarker;
        overlay.separateScale = priceScaling;
        chart.UpdateOverlay();
    }
    static show(chart, callback, closeCallback) {
        if (!instance) {
            instance = new TerceraAddOverlayScreen();
            void instance.set({ name: 'addOverlayScreen' });
            instance.on('teardown', function () {
                instance = null;
            });
            MainWindowManager.AddControl(instance);
        }
        instance.callback = callback;
        instance.closeCallback = closeCallback;
        instance.populateSourceType(chart);
        instance.setParentPanel(chart.terceraChartPanelContext);
        instance.setFocus();
    }
    static addOverlayScreenHandlerInitialize() {
        addOverlayScreenHandler.editOverlay = TerceraAddOverlayScreen.editOverlay;
    }
}
class HistoryTypeComparer {
    compare(t1, t2) {
        const cmpObj = historyTypeComparer;
        return cmpObj.getValue(t1) - cmpObj.getValue(t2);
    }
    getValue(historyType) {
        switch (historyType) {
            case HistoryType.DEFAULT: // default by instrument
                return -1;
            case HistoryType.BID:
                return 0;
            case HistoryType.ASK:
                return 1;
            case HistoryType.LAST:
                return 4;
        }
        return 0;
    }
}
const historyTypeComparer = new HistoryTypeComparer();
TerceraWindowBase.extendWith(TerceraAddOverlayScreen, {
    data: function () {
        return {
            width: 400,
            height: null,
            resizable: false,
            // For using in templates.
            Resources,
            showFooter: false,
            isAutoCloseOld: true,
            colorPickerProps: null,
            showPriceMarker: false
        };
    },
    partials: {
        bodyPartial: TerceraAddOverlayScreenTemplate,
        outScreensPart: TerceraAddOverlayScreenOutScreensTemplate
    }
});
